import axios from 'axios';
import Swal from 'sweetalert2';

const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'development' ? 'http://localhost:8000/api' : 'https://lms.appy.pk/api');

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Add a request interceptor for authentication
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add a response interceptor for global error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // If the request was cancelled, ignore
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    if (error.response) {
      if (error.response.status === 403) {
        const message = error.response.data.message || '';
        if (message.toLowerCase().includes('limit reached')) {
          Swal.fire({
            icon: 'warning',
            title: 'Limit Reached',
            text: message,
            footer: '<div style="text-align:center">Please contact us at <a href="mailto:hello@appy.pk" style="text-decoration:none; color:#4361ee;"><b>hello@appy.pk</b></a> or call <a href="tel:+923019888202" style="text-decoration:none; color:#4361ee;"><b>+92 301 9888 202</b></a> to upgrade your package and increase your limits.</div>',
            confirmButtonColor: '#4361ee',
            confirmButtonText: 'Got it'
          });
          error.isLimitError = true;
          error.isHandled = true; // Mark the error as handled by the interceptor
          return Promise.reject(error);
        }
      }

      // Global error handler for server errors (500 and above)
      if (error.response.status >= 500) {
        Swal.fire({
          icon: 'error',
          title: `Server Error (${error.response.status})`,
          text: error.response.data?.message || 'An unexpected error occurred on the server. Please try again later.',
          confirmButtonColor: '#d33'
        });
        error.isHandled = true;
      }

      // Global error handler for 401 Unauthorized
      if (error.response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
        error.isHandled = true;
      }

      // Global error handler for other unhandled errors like 404, 400 (excluding 401 unauthorized or 422 validations, those are usually handled by components)
      if (!error.isHandled && ![401, 422].includes(error.response.status)) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response.data?.message || error.response.data?.error || 'Something went wrong.',
          confirmButtonColor: '#d33'
        });
        error.isHandled = true;
      }

    } else if (error.request) {
      // The request was made but no response was received (Network error)
      Swal.fire({
        icon: 'error',
        title: 'Network Error',
        text: 'Unable to connect to the server. Please check your internet connection.',
        confirmButtonColor: '#d33'
      });
      error.isHandled = true;
    }

    return Promise.reject(error);
  }
);

export const StudentService = {
  getAll: () => api.get('/students'),
  get: (id) => api.get(`/students/${id}`),
  create: (data) => api.post('/students', data, {
    headers: data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {}
  }),
  update: (id, data) => api.post(`/students/${id}?_method=PUT`, data, {
    headers: data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {}
  }),
  delete: (id) => api.delete(`/students/${id}`),
  importStudents: (data) => api.post('/students/import', data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

export const EnquiryService = {
  getAll: () => api.get('/enquiries'),
  create: (data) => api.post('/enquiries', data),
  update: (id, data) => api.put(`/enquiries/${id}`, data),
};

export const CourseService = {
  getAll: () => api.get('/courses'),
  create: (data) => api.post('/courses', data),
  update: (id, data) => api.put(`/courses/${id}`, data),
  delete: (id) => api.delete(`/courses/${id}`),
  createBatch: (data) => api.post('/batches', data),
  updateBatch: (id, data) => api.put(`/batches/${id}`, data),
  deleteBatch: (id) => api.delete(`/batches/${id}`),
};

export const FeeService = {
  getAll: () => api.get('/fees'),
  create: (data) => api.post('/fees', data),
  update: (id, data) => api.put(`/fees/${id}`, data),
  generate: (data) => api.post('/fees/generate', data),
  bulkGenerate: (data) => api.post('/fees/bulk-generate', data),
};

export const StaffService = {
  getAll: () => api.get('/staff'),
  create: (data) => api.post('/staff', data, {
    headers: data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {}
  }),
  update: (id, data) => api.post(`/staff/${id}?_method=PUT`, data, {
    headers: data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {}
  }),
  delete: (id) => api.delete(`/staff/${id}`),
};

export const PayrollService = {
  getAll: () => api.get('/payroll'),
  create: (data) => api.post('/payroll', data),
  update: (id, data) => api.put(`/payroll/${id}`, data),
};

export const StaffAttendanceService = {
  getAll: (date) => api.get(`/staff-attendance${date ? `?date=${date}` : ''}`),
  save: (data) => api.post('/staff-attendance', data),
};

export const ExamService = {
  getAll: () => api.get('/exams'),
  create: (data) => api.post('/exams', data),
  delete: (id) => api.delete(`/exams/${id}`),
  getStudents: (id) => api.get(`/exams/${id}/students`),
  saveResults: (id, data) => api.post(`/exams/${id}/results`, data),
};

export const LMSService = {
  getStats: () => api.get('/lms/stats'),
  getAssignments: () => api.get('/lms/assignments'),
  createAssignment: (data) => api.post('/lms/assignments', data),
  getMaterials: () => api.get('/lms/materials'),
  createMaterial: (data) => api.post('/lms/materials', data),
};

export const InventoryCategoryService = {
  getAll: () => api.get('/inventory-categories'),
  create: (data) => api.post('/inventory-categories', data),
  update: (id, data) => api.put(`/inventory-categories/${id}`, data),
  delete: (id) => api.delete(`/inventory-categories/${id}`),
};

export const InventoryService = {
  getAll: () => api.get('/inventory'),
  create: (data) => api.post('/inventory', data),
  update: (id, data) => api.put(`/inventory/${id}`, data),
  delete: (id) => api.delete(`/inventory/${id}`),
};

export const ExpenseService = {
  getAll: () => api.get('/expenses'),
  create: (data) => api.post('/expenses', data),
  update: (id, data) => api.put(`/expenses/${id}`, data),
  delete: (id) => api.delete(`/expenses/${id}`),
};

export const UserService = {
  updateProfile: (data) => api.post('/user/profile', data, {
    headers: { 'Content-Type': null }
  }),
  changePassword: (data) => api.post('/change-password', data),
};

export const SettingsService = {
  getSettings: () => api.get('/settings'),
  updateSettings: (data) => api.post('/settings', data, {
    headers: { 'Content-Type': null }
  }),
};

export const ActivityLogService = {
  getAll: (params) => api.get('/activity-logs', { params }),
};

export default api;
